import { createSignal, For, Show } from 'solid-js';

// Bantuan — in-app help written for a regular office worker (non-IT). Plain
// language, short concrete steps, a small glossary first. Hand-written JSX (no
// markdown dependency, per the project's supply-chain stance). The callouts
// repeat the few things people most often get wrong.

function Step(props) {
  return <li class="help-step">{props.children}</li>;
}

function Callout(props) {
  return (
    <div class={`help-callout ${props.tone || 'info'}`}>
      <b>{props.title || 'Catatan'}</b>
      <div>{props.children}</div>
    </div>
  );
}

const TOPICS = [
  {
    id: 'istilah', label: 'Istilah penting',
    body: () => (
      <>
        <p class="ky-vars-note">Beberapa kata yang sering muncul di aplikasi ini. Tidak perlu dihafal — cukup tahu artinya.</p>
        <ul class="help-list">
          <Step><b>Skema gaji</b> — kelompok aturan gaji. Setiap karyawan ikut satu skema, misalnya <b>OFFICE</b> (kantor) atau <b>SALES</b>.</Step>
          <Step><b>Komponen</b> — satu baris di slip gaji, misalnya “Gaji Pokok” atau “Potongan Terlambat”.</Step>
          <Step><b>Variabel</b> — angka yang dipakai untuk menghitung, misalnya berapa Gaji Pokok seorang karyawan.</Step>
          <Step><b>Pemrosesan Gaji</b> — satu kali menghitung gaji untuk satu periode (misalnya gaji bulan Mei).</Step>
          <Step><b>Draft</b> — pemrosesan gaji yang masih bisa diubah. <b>Final</b> — sudah dikunci, tidak bisa diubah lagi.</Step>
          <Step><b>Slip</b> — rincian gaji satu karyawan yang bisa dicetak atau diunduh.</Step>
        </ul>
      </>
    ),
  },
  {
    id: 'gaji', label: 'Menghitung gaji bulanan',
    body: () => (
      <>
        <p class="ky-vars-note">Langkah memproses gaji untuk satu periode. Ini pekerjaan utama setiap bulan.</p>
        <ol class="help-list">
          <Step>Buka menu <b>Pemrosesan Gaji</b>. Pilih skema (misalnya OFFICE), pastikan tanggal <b>Periode mulai</b> sudah benar, lalu klik <b>Buat Pemrosesan Gaji</b>.</Step>
          <Step><i>Jika memakai mesin absen:</i> di bagian <b>Rekap Absensi</b>, klik <b>Tarik &amp; tinjau</b>, periksa jumlah hadir/terlambatnya, lalu klik <b>Terapkan</b>. Angka kehadiran terisi otomatis.</Step>
          <Step>Isi angka lain yang perlu di tabel <b>Input per periode</b> (misalnya denda atau bonus) — cukup ketik di kotaknya.</Step>
          <Step>Klik <b>Hitung</b>. Aplikasi menghitung gaji semua karyawan.</Step>
          <Step>Periksa <b>Hasil per karyawan</b> (klik baris untuk melihat rinciannya).</Step>
          <Step>Kalau sudah benar, klik <b>Finalisasi</b> untuk mengunci.</Step>
        </ol>
        <Callout tone="warn" title="Ingat">
          Kalau Anda mengubah angka di tabel, <b>klik Hitung lagi</b> — hasil tidak ikut berubah dengan sendirinya. Bila muncul kotak peringatan kuning, itu artinya Anda perlu menghitung ulang dulu.
        </Callout>
        <Callout tone="warn" title="Penting">
          <b>Finalisasi tidak bisa dibatalkan.</b> Jika menemukan kesalahan setelah final, buat <b>pemrosesan gaji baru</b> untuk periode yang sama, lalu isi ulang yang benar.
        </Callout>
      </>
    ),
  },
  {
    id: 'slip', label: 'Mencetak slip & laporan',
    body: () => (
      <>
        <p class="ky-vars-note">Setelah gaji difinalisasi, Anda bisa mencetak slip dan membuat laporan.</p>
        <ol class="help-list">
          <Step>Buka menu <b>Slip Gaji</b>, pilih skema lalu periode.</Step>
          <Step>Klik nama karyawan untuk melihat slipnya, lalu <b>Cetak</b>. Untuk mencetak semua sekaligus, klik <b>Cetak Semua</b>.</Step>
          <Step>Untuk rekap total dan daftar transfer bank, buka menu <b>Laporan</b>. Tombol <b>Export CSV</b> menghasilkan berkas yang bisa dibuka di Excel.</Step>
        </ol>
        <Callout>
          Anda bisa menyembunyikan baris tertentu di slip (tombol <b>Atur Tampilan</b>), tetapi <b>total gaji tetap angka yang sebenarnya</b> — menyembunyikan hanya menghilangkannya dari cetakan, bukan dari perhitungan.
        </Callout>
        <Callout>
          Di Laporan, perhatikan tanda <b>“tanpa rekening”</b>: karyawan itu belum punya data bank, jadi tidak ikut dalam daftar transfer.
        </Callout>
      </>
    ),
  },
  {
    id: 'karyawan', label: 'Data karyawan',
    body: () => (
      <>
        <p class="ky-vars-note">Menambah karyawan baru dan mengubah datanya.</p>
        <ol class="help-list">
          <Step>Buka menu <b>Karyawan</b>. Klik <b>+ Karyawan</b> untuk menambah, atau pilih seorang karyawan lalu <b>Edit profil</b> untuk mengubah.</Step>
          <Step>Departemen dan posisi <b>dipilih dari daftar</b> (bukan diketik bebas), supaya seragam dan tidak salah ketik.</Step>
          <Step>Untuk mengubah gaji pokok atau tunjangan seseorang, atur <b>Variabel gaji</b> di panel sebelah kanan.</Step>
        </ol>
        <Callout>Perubahan data karyawan berlaku untuk <b>pemrosesan gaji berikutnya</b> — yang sudah difinalisasi tidak ikut berubah.</Callout>
        <p>Daftar departemen dan posisi dikelola di menu <b>Organisasi</b>. Aturan/rumus gaji ada di <b>Komponen Gaji</b> (untuk pengguna mahir; selalu coba dulu lewat tombol <b>Uji Coba</b> sebelum menyimpan).</p>
      </>
    ),
  },
  {
    id: 'absensi', label: 'Absensi & hari libur',
    body: () => (
      <>
        <p class="ky-vars-note">Sumber data kehadiran dan kalender libur.</p>
        <ul class="help-list">
          <Step>Menu <b>Absensi → Mesin</b>: daftar mesin sidik jari. <b>Data Mentah</b>: catatan scan yang masuk.</Step>
          <Step>Menu <b>Absensi → Hari Libur</b>: isi tanggal-tanggal libur. Hari libur dan hari Minggu dihitung sebagai hari libur berbayar.</Step>
        </ul>
        <Callout tone="warn" title="Ingat">Perbarui daftar <b>Hari Libur setiap awal tahun</b> agar perhitungan kehadiran benar.</Callout>
      </>
    ),
  },
  {
    id: 'portal', label: 'Portal karyawan',
    body: () => (
      <>
        <p class="ky-vars-note">Karyawan bisa masuk sendiri untuk melihat slip dan mengajukan lembur atau izin. Ini yang perlu Anda sampaikan kepada mereka.</p>
        <ol class="help-list">
          <Step>Masuk memakai <b>NIK</b> dan password awal <b>karyawan</b>.</Step>
          <Step><b>Wajib mengganti password</b> lebih dulu, baru bisa melihat slip.</Step>
          <Step>Melihat dan mencetak slip gaji (yang sudah final).</Step>
          <Step>Mengajukan <b>Lembur</b> atau <b>Izin/Cuti/Sakit</b>, boleh melampirkan foto atau PDF.</Step>
          <Step>Atasan menyetujui pengajuan dari menu <b>Persetujuan</b>.</Step>
        </ol>
        <Callout>Pengajuan yang sudah <b>disetujui</b> otomatis ikut diperhitungkan saat gaji dihitung. Tidak ada tombol batal di portal — bila keliru, hubungi bagian HR.</Callout>
      </>
    ),
  },
  {
    id: 'pengaturan', label: 'Pengguna & pengaturan',
    body: () => (
      <>
        <p class="ky-vars-note">Bagian ini biasanya dikerjakan oleh admin utama.</p>
        <h4 class="kg-hist-title">Pengguna (akun admin)</h4>
        <ol class="help-list">
          <Step>Buka <b>Pengaturan → Pengguna → + pengguna</b>, isi nama dan password.</Step>
          <Step><b>Centang minimal satu perusahaan</b> untuk pengguna itu — tanpa ini ia tidak bisa masuk.</Step>
        </ol>
        <h4 class="kg-hist-title">Pengaturan umum</h4>
        <ul class="help-list">
          <Step><b>Desimal uang</b> — berapa angka di belakang koma saat menampilkan uang (0–4).</Step>
          <Step><b>Lama sesi login</b> — berapa jam sebelum admin otomatis keluar.</Step>
          <Step><b>Izin &amp; Cuti</b> — apakah Cuti/Sakit/Izin dihitung sebagai hadir.</Step>
          <Step><b>Catatan kaki slip</b> — teks kecil di bawah slip.</Step>
        </ul>
        <Callout>Akun admin pertama dibuat otomatis saat aplikasi pertama dijalankan; passwordnya muncul di layar server — minta tim IT, lalu segera ganti di Pengaturan → Pengguna.</Callout>
      </>
    ),
  },
];

export default function Bantuan() {
  const [active, setActive] = createSignal(TOPICS[0].id);
  const current = () => TOPICS.find((t) => t.id === active()) ?? TOPICS[0];
  return (
    <>
      <div class="page-head"><h2>Bantuan</h2></div>
      <p class="ky-vars-note" style="margin:-4px 0 14px">
        Panduan singkat memakai aplikasi. Pilih topik di kiri. Mulai dari
        <b> “Istilah penting”</b> bila Anda baru pertama memakainya.
      </p>
      <div class="help-layout">
        <nav class="card help-nav">
          <For each={TOPICS}>{(t) =>
            <button class="help-nav-item" classList={{ active: active() === t.id }}
              onClick={() => setActive(t.id)}>{t.label}</button>
          }</For>
        </nav>
        <div class="card card-pad help-content">
          <Show when={current()} keyed>{(t) => t.body()}</Show>
        </div>
      </div>
    </>
  );
}
